"use client";

import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import {
  AppstoreOutlined,
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import blueflashLogo from "../assets/logo/blueflashlogo.png";
import spotflashLogo from "../assets/logo/spotflashlogo.png";
import docflashLogo from "../assets/logo/docflashlogo.png";

import userServices from "../services/auth.service";

const POSITION_KEY = "blueflash-app-switcher-position";

const DEFAULT_POSITION = {
  right: 20,
  bottom: 20,
};

const APP_META = {
  blueflash: {
    color: "#2563eb",
    logo: blueflashLogo,
  },

  spotflash: {
    color: "#7c3aed",
    logo: spotflashLogo,
  },

  docflash: {
    color: "#db2777",
    logo: docflashLogo,
  },
};

const FALLBACK_COLORS = [
  "#0891b2",
  "#ea580c",
  "#16a34a",
  "#9333ea",
];

/**
 * Convert API applications into applications
 * that can be displayed by AppSwitcher.
 *
 * Expected API structure:
 *
 * {
 *   app_id,
 *   name,
 *   app_url,
 *   parent_app_id,
 *   logo_url,
 *   description,
 *   sort_order,
 *   is_active
 * }
 */
const toLaunchable = (list = []) => {
  const active = list.filter(
    (app) => app?.is_active !== false,
  );

  const appName = String(
    import.meta.env.VITE_APP_NAME || "",
  )
    .trim()
    .toLowerCase();

  if (!appName) {
    console.warn(
      "AppSwitcher: VITE_APP_NAME is not configured.",
    );

    return [];
  }

  /**
   * Find the current platform.
   *
   * Example:
   *
   * VITE_APP_NAME=BlueFlash Platform
   *
   * Finds:
   *
   * BlueFlash Platform
   */
  const currentPlatform = active.find(
    (app) =>
      String(app?.name || "")
        .trim()
        .toLowerCase() === appName,
  );

  if (!currentPlatform) {
    console.warn(
      `AppSwitcher: Platform "${import.meta.env.VITE_APP_NAME}" was not found.`,
    );

    return [];
  }

  /**
   * Find children of the current platform.
   */
  const children = active
    .filter(
      (app) =>
        String(app?.parent_app_id || "") ===
        String(currentPlatform?.app_id || ""),
    )
    .sort(
      (a, b) =>
        (a?.sort_order ?? 0) -
        (b?.sort_order ?? 0),
    );

  /**
   * If platform has children,
   * show children.
   */
  if (children.length > 0) {
    return children.map((app, index) => {
      const key = String(
        app?.name || "",
      )
        .trim()
        .toLowerCase();

      const meta = APP_META[key] || {};

      return {
        key: app.app_id,
        appId: app.app_id,
        label: app.name,
        path: app.app_url,
        description: app.description || "",

        color:
          meta.color ||
          FALLBACK_COLORS[
            index % FALLBACK_COLORS.length
          ],

        /**
         * IMPORTANT:
         *
         * Do NOT use app.logo_url here because
         * your API currently returns the app URL
         * instead of an image URL.
         */
        logo: meta.logo || null,
      };
    });
  }

  /**
   * If the platform has no children,
   * show the platform itself.
   */
  const platformKey = String(
    currentPlatform?.name || "",
  )
    .trim()
    .toLowerCase();

  const meta = APP_META[platformKey] || {};

  return [
    {
      key: currentPlatform.app_id,
      appId: currentPlatform.app_id,
      label: currentPlatform.name,
      path: currentPlatform.app_url,
      description:
        currentPlatform.description || "",

      color:
        meta.color || FALLBACK_COLORS[0],

      logo: meta.logo || null,
    },
  ];
};

const getSavedPosition = () => {
  if (typeof window === "undefined") {
    return DEFAULT_POSITION;
  }

  try {
    const saved =
      localStorage.getItem(POSITION_KEY);

    if (!saved) {
      return DEFAULT_POSITION;
    }

    const parsed = JSON.parse(saved);

    if (
      typeof parsed?.right === "number" &&
      typeof parsed?.bottom === "number"
    ) {
      return parsed;
    }
  } catch (error) {
    console.warn(
      "AppSwitcher: Failed to restore position.",
      error,
    );
  }

  return DEFAULT_POSITION;
};

export default function AppSwitcher() {
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [position, setPosition] =
    useState(getSavedPosition);

  const draggingRef = useRef(false);
  const movedRef = useRef(false);

  /**
   * --------------------------------------------------
   * LOAD APPLICATIONS
   * --------------------------------------------------
   */
  useEffect(() => {
    let mounted = true;

    const loadApps = async () => {
      try {
        setLoading(true);

        const response =
          await userServices.getApplications();

        /**
         * Supports both:
         *
         * response.data
         *
         * and
         *
         * response.data.data
         */
        const data =
          response?.data?.data ??
          response?.data ??
          [];

        if (!mounted) {
          return;
        }

        const launchable =
          toLaunchable(data);

        setApps(launchable);
      } catch (error) {
        console.error(
          "AppSwitcher: Failed to load applications.",
          error,
        );

        if (mounted) {
          setApps([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadApps();

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * --------------------------------------------------
   * SAVE POSITION
   * --------------------------------------------------
   */
  useEffect(() => {
    try {
      localStorage.setItem(
        POSITION_KEY,
        JSON.stringify(position),
      );
    } catch {
      // Ignore localStorage errors.
    }
  }, [position]);

  /**
   * --------------------------------------------------
   * DRAG START
   * --------------------------------------------------
   */
  const handlePointerDown = (event) => {
    /**
     * Only primary mouse button.
     */
    if (
      event.pointerType === "mouse" &&
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();

    draggingRef.current = true;
    movedRef.current = false;

    const startX = event.clientX;
    const startY = event.clientY;

    const startRight = position.right;
    const startBottom = position.bottom;

    const handleMove = (moveEvent) => {
      if (!draggingRef.current) {
        return;
      }

      const dx =
        moveEvent.clientX - startX;

      const dy =
        moveEvent.clientY - startY;

      if (
        Math.abs(dx) > 3 ||
        Math.abs(dy) > 3
      ) {
        movedRef.current = true;
      }

      /**
       * Button size = 36px.
       *
       * Keep it inside viewport.
       */
      const buttonSize = 36;

      const maxRight = Math.max(
        8,
        window.innerWidth -
          buttonSize -
          8,
      );

      const maxBottom = Math.max(
        8,
        window.innerHeight -
          buttonSize -
          8,
      );

      const nextRight = Math.max(
        8,
        Math.min(
          maxRight,
          startRight - dx,
        ),
      );

      const nextBottom = Math.max(
        8,
        Math.min(
          maxBottom,
          startBottom - dy,
        ),
      );

      setPosition({
        right: nextRight,
        bottom: nextBottom,
      });
    };

    const handleUp = () => {
      draggingRef.current = false;

      window.removeEventListener(
        "pointermove",
        handleMove,
      );

      window.removeEventListener(
        "pointerup",
        handleUp,
      );
    };

    window.addEventListener(
      "pointermove",
      handleMove,
    );

    window.addEventListener(
      "pointerup",
      handleUp,
    );
  };

  /**
   * --------------------------------------------------
   * BUTTON CLICK
   * --------------------------------------------------
   */
  const handleButtonClick = () => {
    /**
     * If pointer movement happened,
     * this was a drag and not a click.
     */
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }

    setOpen((value) => !value);
  };

  /**
   * --------------------------------------------------
   * OPEN APPLICATION
   * --------------------------------------------------
   */
  const openApp = async (app) => {
    if (!app?.appId) {
      return;
    }

    try {
      setOpen(false);

      /**
       * Existing backend auto-login flow.
       */
      const response =
        await userServices.generateAutoLoginLink(
          app.appId,
        );

      /**
       * Support common response structures.
       */
      const token =
        response?.data?.auto_login_token ??
        response?.data?.data?.auto_login_token ??
        response?.auto_login_token;

      if (!token) {
        message.error(
          "Unable to generate login link.",
        );

        return;
      }

      /**
       * app.path comes from app_url.
       */
      if (!app.path) {
        message.error(
          "Application URL is not configured.",
        );

        return;
      }

      const url = new URL(
        app.path,
        window.location.origin,
      );

      /**
       * Ensure /auto-login is appended only once.
       */
      const pathname =
        url.pathname.replace(/\/+$/, "");

      if (!pathname.endsWith("/auto-login")) {
        url.pathname =
          `${pathname}/auto-login`;
      }

      /**
       * Add login token.
       */
      url.searchParams.set(
        "token",
        token,
      );

      window.location.assign(
        url.toString(),
      );
    } catch (error) {
      console.error(
        "AppSwitcher: Failed to open application.",
        error,
      );

      message.error(
        "Unable to open application.",
      );
    }
  };

  /**
   * Don't render anything if there are
   * no applications.
   */
  if (!loading && apps.length === 0) {
    return null;
  }

  return (
    <div
      className="fixed z-[99999]"
      style={{
        right: `${position.right}px`,
        bottom: `${position.bottom}px`,
      }}
    >
      {/* ============================================
          APP LIST
          ============================================ */}
      {open && (
        <div
          className="
            absolute
            bottom-[44px]
            right-0
            w-[190px]
            overflow-hidden
            rounded-xl
            border
            border-gray-200
            bg-white
            p-1
            shadow-[0_8px_28px_rgba(0,0,0,0.18)]
          "
        >
          {/* Header */}
          <div
            className="
              flex
              items-center
              justify-between
              px-2
              py-1.5
            "
          >
            <span
              className="
                text-[10px]
                font-semibold
                uppercase
                tracking-[0.08em]
                text-gray-400
              "
            >
              Applications
            </span>

            {loading && (
              <LoadingOutlined
                className="text-[11px] text-gray-400"
              />
            )}
          </div>

          {/* Apps */}
          <div className="space-y-[2px]">
            {apps.map((app) => (
              <button
                key={
                  app.appId || app.key
                }
                type="button"
                onClick={() =>
                  openApp(app)
                }
                className="
                  flex
                  w-full
                  items-center
                  gap-2
                  rounded-lg
                  px-2
                  py-1.5
                  text-left
                  transition
                  hover:bg-gray-100
                  active:bg-gray-200
                "
              >
                {/* Logo */}
                <div
                  className="
                    flex
                    h-7
                    w-7
                    shrink-0
                    items-center
                    justify-center
                    overflow-hidden
                    rounded-md
                  "
                  style={{
                    backgroundColor: `${
                      app.color ||
                      "#2563eb"
                    }15`,
                  }}
                >
                  {app.logo ? (
                    <img
                      src={app.logo}
                      alt=""
                      draggable={false}
                      className="
                        h-5
                        w-5
                        object-contain
                      "
                    />
                  ) : (
                    <span
                      className="
                        text-[10px]
                        font-bold
                      "
                      style={{
                        color:
                          app.color ||
                          "#2563eb",
                      }}
                    >
                      {String(
                        app.label || "",
                      )
                        .slice(0, 2)
                        .toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Name */}
                <span
                  className="
                    min-w-0
                    flex-1
                    truncate
                    text-[12px]
                    font-medium
                    text-gray-700
                  "
                >
                  {app.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ============================================
          FLOATING BUTTON
          ============================================ */}
      <button
        type="button"
        aria-label="Applications"
        title="Applications"
        onPointerDown={
          handlePointerDown
        }
        onClick={
          handleButtonClick
        }
        className="
          flex
          h-9
          w-9
          select-none
          items-center
          justify-center
          rounded-full
          border
          border-white/30
          bg-[#1B4FD8]
          text-white
          shadow-[0_4px_14px_rgba(0,0,0,0.22)]
          transition
          hover:scale-105
          hover:shadow-[0_5px_18px_rgba(0,0,0,0.26)]
          active:scale-95
        "
        style={{
          cursor: draggingRef.current
            ? "grabbing"
            : "grab",
          touchAction: "none",
        }}
      >
        {open ? (
          <CloseOutlined className="text-[13px]" />
        ) : loading ? (
          <LoadingOutlined className="text-[14px]" />
        ) : (
          <AppstoreOutlined className="text-[15px]" />
        )}
      </button>
    </div>
  );
}